import apiService from "../../../services/api-client";
import { Alert, AlertStatistics } from "../interfaces/alerts";

interface GetAlertsResponse {
  alerts: Alert[];
  total: number;
}

interface ResolveAlertResponse {
  status: string;
  alert_id: number;
  resolved_at: string;
}

interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

/**
 * AlertsApiService
 * Handles all API calls related to stock alerts
 * Provides methods for fetching alerts, statistics, and resolving alerts
 */
export const alertsApiService = {
  /**
   * Fetch alerts with optional filters and pagination
   * @param companyId - The company ID
   * @param params - Optional parameters (severity, limit, offset)
   * @returns Promise with alerts array and total count
   * @throws Error if API call fails
   */
  async getAlerts(
    companyId: string,
    params?: {
      severity?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<GetAlertsResponse> {
    try {
      const { data } = await apiService().get<any>(
        `/skualo/${companyId}/alerts`,
        {
          params: {
            limit: params?.limit || 100,
            offset: params?.offset || 0,
            ...(params?.severity && { severity: params.severity }),
          },
        }
      );

      console.log("📋 Backend alerts response:", data);

      // Backend returns: { status: "ok", alerts: [...], total_alerts: 15 }
      // Frontend expects: { alerts: [...], total: 15 }
      const response: GetAlertsResponse = {
        alerts: data.alerts || [],
        total: data.total_alerts || data.total || 0,
      };

      // Validate response structure
      if (!Array.isArray(response.alerts)) {
        throw new Error("Invalid response: alerts is not an array");
      }

      if (typeof response.total !== "number") {
        throw new Error("Invalid response: total is not a number");
      }

      console.log("✅ Mapped alerts:", response);

      return response;
    } catch (error: any) {
      console.error("❌ Error fetching alerts:", error);
      const apiError: ApiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          "Error fetching alerts",
        status: error.response?.status,
        code: error.code,
      };
      throw apiError;
    }
  },

  /**
   * Fetch alert statistics (counts by severity)
   * @param companyId - The company ID
   * @returns Promise with alert statistics
   * @throws Error if API call fails
   */
  async getStatistics(companyId: string): Promise<AlertStatistics> {
    try {
      const { data } = await apiService().get<any>(
        `/skualo/${companyId}/alerts/statistics`
      );

      console.log("📊 Backend response:", data);

      // Validate response structure
      if (typeof data.total_active !== "number") {
        throw new Error("Invalid response: total_active is not a number");
      }

      // Map backend response to frontend format
      // Backend returns: { critical: 3, high: 5, medium: 4, low: 3 }
      // Frontend expects: { critical_count: 3, high_count: 5, medium_count: 4, low_count: 3 }
      const statistics: AlertStatistics = {
        total_active: data.total_active,
        critical_count: data.by_severity?.critical || data.by_severity?.CRITICAL || 0,
        high_count: data.by_severity?.high || data.by_severity?.HIGH || 0,
        medium_count: data.by_severity?.medium || data.by_severity?.MEDIUM || 0,
        low_count: data.by_severity?.low || data.by_severity?.LOW || 0,
        total_resolved: data.total_resolved || 0,
        by_severity: {
          CRITICAL: data.by_severity?.critical || data.by_severity?.CRITICAL || 0,
          HIGH: data.by_severity?.high || data.by_severity?.HIGH || 0,
          MEDIUM: data.by_severity?.medium || data.by_severity?.MEDIUM || 0,
          LOW: data.by_severity?.low || data.by_severity?.LOW || 0,
        },
      };

      console.log("✅ Mapped statistics:", statistics);

      return statistics;
    } catch (error: any) {
      console.error("❌ Error fetching statistics:", error);
      const apiError: ApiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          "Error fetching alert statistics",
        status: error.response?.status,
        code: error.code,
      };
      throw apiError;
    }
  },

  /**
   * Resolve an alert by marking it as resolved
   * @param companyId - The company ID
   * @param alertId - The alert ID to resolve
   * @returns Promise with resolve response
   * @throws Error if API call fails
   */
  async resolveAlert(
    companyId: string,
    alertId: number
  ): Promise<ResolveAlertResponse> {
    try {
      if (!companyId || !alertId) {
        throw new Error("Company ID and Alert ID are required");
      }

      const { data } = await apiService().post<ResolveAlertResponse>(
        `/skualo/${companyId}/alerts/${alertId}/resolve`
      );

      // Validate response structure
      if (data.status !== "ok") {
        throw new Error("Invalid response: status is not ok");
      }

      if (typeof data.alert_id !== "number") {
        throw new Error("Invalid response: alert_id is not a number");
      }

      return data;
    } catch (error: any) {
      const apiError: ApiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          "Error resolving alert",
        status: error.response?.status,
        code: error.code,
      };
      throw apiError;
    }
  },
};
